// modules/clientes/components/ServicioEditDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Grid, TextField, MenuItem, Autocomplete, CircularProgress,
  Switch, FormControlLabel, Button, Alert, Chip, Typography, Tabs, Tab,
  Dialog, DialogContent, DialogActions, Divider,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import { ESTADOS_COLOR } from "../../../utils/constants";
import * as clienteService from "../services/clienteService";
import { FotoPerfilServicio, ImagenesServicioEditor, useImagenesEdicion } from "./ImagenesServicio";
import * as grupoService from "../../grupos/services/grupoService";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import { PAGO_OPTIONS } from "../constants/pago";
import { usePlataformasYTecnicos } from "../../requerimiento/hooks/usePlataformasYTecnicos";

const MONEDAS = [{ value: 1, label: "Dolares (USD)" }, { value: 2, label: "Bolivianos (BOB)" }];
const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const TIPOS_SERV = ["Nacional", "Sucursal"];

// La Paz y La Paz(El Alto) se tratan como una sola "zona" para el listado de
// ejecutivos de ventas: un usuario logueado en cualquiera de las dos ve los
// ejecutivos de ambas combinados (antes solo veia los de su propia sucursal).
const GRUPOS_SUCURSAL_EJECUTIVOS = [["La Paz", "La Paz(El Alto)"]];
const sucursalesParaEjecutivos = (sucursal) => {
  const grupo = GRUPOS_SUCURSAL_EJECUTIVOS.find((g) => g.includes(sucursal));
  return grupo ? grupo.join(",") : sucursal;
};

const PU_LABELS_BY_SERVICIO = {
  1: {
    pu_1: "P.U. Portatil",
    pu_2: "P.U. Movil",
    pu_4: "P.U. Base",
    pu_3: "P.U. Servicio",
  },
  2: {
    pu_1: "P.U. Alquiler",
    pu_2: "P.U. Servicio Tecnico",
    pu_4: "P.U. Servicio Nacional",
    pu_3: "P.U. Servicio Internacional",
  },
};

const getPuLabels = (codServicio) => PU_LABELS_BY_SERVICIO[Number(codServicio)] || null;

const DIALOG_COMPACT_SX = {
  pt: 1,
  "& .MuiTextField-root .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: 36,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
  },
  "& .MuiInputBase-input:not(textarea)": {
    py: "7px",
  },
  "& .MuiSelect-select": {
    py: "7px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const getEjecutivoLabel = (option) =>
  option?.nombre ? `${option.nombre} (${option.username})` : option?.username || "";

const getEjecutivoValue = (ejecutivos, username) =>
  ejecutivos.find((u) => u.username === username) ||
  (username ? { username, nombre: username } : null);

const PreciosServicioFields = ({ codServicio, values, onChange, disabled }) => {
  const labels = getPuLabels(codServicio);
  if (!labels) return null;

  return (
    <>
      {Object.entries(labels).map(([name, label]) => (
        <Grid key={name} size={{ xs: 6, sm: 3 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label={label}
            name={name}
            value={values[name] ?? ""}
            onChange={onChange}
            disabled={disabled}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Grid>
      ))}
    </>
  );
};

const GrupoAutocomplete = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    if (!open) return;

    let activo = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await grupoService.buscar(inputValue.trim());
        if (activo) setOptions(Array.isArray(data) ? data : []);
      } catch {
        if (activo) setOptions([]);
      } finally {
        if (activo) setLoading(false);
      }
    }, 250);

    return () => {
      activo = false;
      clearTimeout(timer);
    };
  }, [open, inputValue]);

  const setGrupo = (nombre) => {
    const limpio = nombre || "";
    setInputValue(limpio);
    onChange({ target: { name: "grupo", value: limpio } });
  };

  return (
    <Autocomplete
      freeSolo
      size="small"
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      inputValue={inputValue}
      value={value || ""}
      filterOptions={(x) => x}
      onInputChange={(_, newInputValue) => setGrupo(newInputValue)}
      onChange={(_, newValue) => {
        setGrupo(typeof newValue === "string" ? newValue : newValue?.nombre || "");
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option?.nombre || ""
      }
      isOptionEqualToValue={(option, selected) =>
        (option?.nombre || option) === (selected?.nombre || selected)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Grupo"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const useEjecutivosTRS = (open) => {
  const { user } = useAuth();
  const usernameActual = user?.username?.trim() || "";
  const sucursalUsuario = user?.sucursal?.trim() || "";
  const sucursalFacturarDefault = SUCURSALES.includes(sucursalUsuario) ? sucursalUsuario : "";
  const [ejecutivos, setEjecutivos] = useState([]);
  const [loadingEjecutivos, setLoadingEjecutivos] = useState(false);

  useEffect(() => {
    if (!open || !sucursalUsuario) {
      setEjecutivos([]);
      return;
    }

    let activo = true;
    const cargarEjecutivos = async () => {
      setLoadingEjecutivos(true);
      try {
        const { data } = await axios.get("/api/usuarios-trs", {
          params: { sucursal: sucursalesParaEjecutivos(sucursalUsuario), estado: "A" },
        });
        if (activo) setEjecutivos(Array.isArray(data) ? data : []);
      } catch {
        if (activo) setEjecutivos([]);
      } finally {
        if (activo) setLoadingEjecutivos(false);
      }
    };

    cargarEjecutivos();
    return () => { activo = false; };
  }, [open, sucursalUsuario]);

  return { ejecutivos, loadingEjecutivos, usernameActual, sucursalFacturarDefault };
};

const ServicioEditDialog = ({ open, servicio, onClose, onSaved }) => {
  const [form,    setForm]    = useState({ ...servicio, grupo: servicio.grupo ?? servicio.grupos ?? "" });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [tab, setTab] = useState(0);
  const imagenes = useImagenesEdicion(servicio.cod_cliente, servicio.cod_servicio_cliente);
  const {
    ejecutivos,
    loadingEjecutivos,
    sucursalFacturarDefault,
  } = useEjecutivosTRS(open);
  const { plataformas, fetchPlataformas } = usePlataformasYTecnicos();

  useEffect(() => {
    if (!open) return;
    setForm((p) => ({
      ...p,
      sucursal_facturar: p.sucursal_facturar || sucursalFacturarDefault,
    }));
  }, [open, sucursalFacturarDefault]);

  useEffect(() => {
    if (open) fetchPlataformas();
  }, [open, fetchPlataformas]);

  const handle = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const grupoNombre = (form.grupo || "").trim();

      const payload = {
        grupo            : grupoNombre,
        nombre           : form.nombre,
        descripcion      : form.descripcion,
        tiempo_conservacion_recorrido: form.tiempo_conservacion_recorrido,
        estado           : form.estado,
        sucursal_facturar: form.sucursal_facturar,
        pago             : form.pago,
        moneda           : form.moneda,
        tipo             : form.tipo,
        ejecutivo_venta  : form.ejecutivo_venta,
        web              : form.web === "" || form.web == null ? null : Number(form.web),
        correo           : form.correo,
        observacion      : form.observacion,
        central_riesgo   : form.central_riesgo === "Cliente Reportado" ? "Cliente Reportado" : "No",
        pu_1             : form.pu_1 || 0,
        pu_2             : form.pu_2 || 0,
        pu_3             : form.pu_3 || 0,
        pu_4             : form.pu_4 || 0,
      };

      await clienteService.updateServicio(
        servicio.cod_cliente,
        servicio.cod_servicio_cliente,
        payload,
      );

      // Las imágenes elegidas se aplican recién ahora, al guardar.
      let imagenPrincipal;
      try {
        imagenPrincipal = await imagenes.aplicar();
      } catch (e) {
        setError("Los datos se guardaron, pero no se pudieron aplicar los cambios de imágenes. Pulsa Guardar para reintentar.");
        return;
      }
      onSaved({ ...servicio, ...payload, grupos: grupoNombre, ...(imagenPrincipal !== undefined ? { imagen_principal: imagenPrincipal } : {}) });
    } catch (err) {
      setError(err?.response?.data?.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      {/* Cabecera tipo perfil: foto del servicio + resumen */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 2.5, py: 1.5, bgcolor: (t) => alpha(t.palette.primary.main, 0.09), borderBottom: 1, borderColor: "divider" }}>
        <FotoPerfilServicio ctrl={imagenes} nombre={servicio.nombre_servicio || servicio.descripcion} size={76} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="overline" sx={{ lineHeight: 1.2, color: "text.secondary", display: "block" }}>
            Editar servicio #{servicio.cod_servicio_cliente}
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }} noWrap>{servicio.nombre_servicio || "Servicio"}</Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }} noWrap>{form.nombre || form.descripcion || "Sin nombre"}</Typography>
          <Box sx={{ display: "flex", gap: 0.75, mt: 0.75, flexWrap: "wrap" }}>
            {form.estado && <Chip size="small" label={form.estado} color={ESTADOS_COLOR[form.estado] || "default"} />}
            {form.tipo && <Chip size="small" variant="outlined" label={form.tipo} />}
            {form.sucursal_facturar && <Chip size="small" variant="outlined" label={form.sucursal_facturar} />}
            {form.central_riesgo === "Cliente Reportado" && <Chip size="small" color="error" label="Central de riesgo" />}
          </Box>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ minHeight: 40, px: 1.5, "& .MuiTab-root": { minHeight: 40, py: 0.5, fontSize: 13, fontWeight: 700, textTransform: "none" } }}
        >
          <Tab icon={<InfoOutlinedIcon fontSize="small" />} iconPosition="start" label="General" />
          <Tab icon={<PaymentsOutlinedIcon fontSize="small" />} iconPosition="start" label="Facturación y precios" />
          <Tab icon={<SupportAgentOutlinedIcon fontSize="small" />} iconPosition="start" label="Comercial" />
          <Tab
            icon={<PhotoLibraryOutlinedIcon fontSize="small" />} iconPosition="start"
            label={`Imágenes${imagenes.visibles.length + imagenes.nuevas.length ? ` (${imagenes.visibles.length + imagenes.nuevas.length})` : ""}${imagenes.hayCambios ? " •" : ""}`}
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ minHeight: 330 }}>
        <Box sx={DIALOG_COMPACT_SX}>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

          {tab === 0 && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <GrupoAutocomplete value={form.grupo ?? ""} onChange={handle} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Nombre" name="nombre" value={form.nombre ?? ""} onChange={handle} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Descripcion" name="descripcion" value={form.descripcion ?? ""} onChange={handle} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Estado" name="estado" value={form.estado ?? ""} onChange={handle} disabled={loading}>
                  {Object.keys(ESTADOS_COLOR).map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Plataforma (Web)" name="web" value={form.web != null ? String(form.web) : ""} onChange={handle} disabled={loading}>
                  <MenuItem value=""><em>Sin plataforma</em></MenuItem>
                  {plataformas.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.nombre}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" label="Tiempo de Conservacion de Recorrido" name="tiempo_conservacion_recorrido" value={form.tiempo_conservacion_recorrido ?? ""} onChange={handle} disabled={loading} />
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Sucursal a Facturar" name="sucursal_facturar" value={form.sucursal_facturar ?? ""} onChange={handle} disabled={loading}>
                  {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Forma de Pago" name="pago" value={form.pago ?? ""} onChange={handle} disabled={loading}>
                  {PAGO_OPTIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Moneda" name="moneda" value={form.moneda ?? ""} onChange={handle} disabled>
                  {MONEDAS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth size="small" label="Tipo de Servicio" name="tipo" value={form.tipo ?? ""} onChange={handle} disabled={loading}>
                  {TIPOS_SERV.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              {getPuLabels(servicio.cod_servicio) && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4, color: "text.secondary", mt: 0.5 }}>PRECIOS UNITARIOS</Typography>
                  </Grid>
                  <PreciosServicioFields codServicio={servicio.cod_servicio} values={form} onChange={handle} disabled={loading} />
                </>
              )}
            </Grid>
          )}

          {tab === 2 && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  size="small"
                  disabled={loading}
                  options={ejecutivos}
                  loading={loadingEjecutivos}
                  value={getEjecutivoValue(ejecutivos, form.ejecutivo_venta)}
                  onChange={(_, value) => {
                    setForm((p) => ({ ...p, ejecutivo_venta: value?.username || "" }));
                  }}
                  getOptionLabel={getEjecutivoLabel}
                  isOptionEqualToValue={(option, value) => option.username === value.username}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ejecutivo de Ventas"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingEjecutivos && <CircularProgress size={14} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Correo" type="email" name="correo" value={form.correo ?? ""} onChange={handle} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" multiline rows={3} label="Observacion" name="observacion" value={form.observacion ?? ""} onChange={handle} disabled={loading} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={(
                    <Switch
                      color="error"
                      checked={form.central_riesgo === "Cliente Reportado"}
                      onChange={(e) => setForm((p) => ({
                        ...p, central_riesgo: e.target.checked ? "Cliente Reportado" : "No",
                      }))}
                      disabled={loading}
                    />
                  )}
                  label="Cliente reportado en Central de Riesgo"
                />
              </Grid>
            </Grid>
          )}

          {tab === 3 && <ImagenesServicioEditor ctrl={imagenes} />}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {imagenes.hayCambios && <Typography sx={{ fontSize: 12, color: "warning.main", fontWeight: 600, mr: "auto" }}>Hay cambios de imágenes sin guardar</Typography>}
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || imagenes.ocupado}
          startIcon={loading ? <CircularProgress size={14} /> : null}>
          {loading ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServicioEditDialog;
